import React, { useState, useEffect, useRef } from 'react';
import { ORDER_STATUS } from '../orderStatus';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import ClientProfileModal from '../components/ClientProfileModal';
import { IconUsers, IconTelegram, IconMax, IconWeb, IconWallet } from '../components/StatIcons';
import Select from '../components/Select';
import { useAuth } from '../context/AuthContext';

// Фильтры списка (владелец 27.10): когда пришёл, через что, откуда, покупки, доходят ли сообщения, группа.
// Из «Статистика → Клиенты» фильтр приходит разовой меткой nf_clients_filter
const PLATFORM_OPTS = [{ value: '', label: 'Все' }, { value: 'telegram', label: 'Telegram' }, { value: 'max', label: 'Max' }, { value: 'web', label: 'Только сайт' }];
const BOUGHT_OPTS = [{ value: '', label: 'Все' }, { value: 'none', label: 'Ещё не покупали' }, { value: 'any', label: 'Покупали' },
  { value: 'one', label: 'Купили 1 раз' }, { value: 'many', label: 'Купили 2 раза и больше' }];
const IDLE_OPTS = [{ value: '', label: 'Неважно' }, { value: '30', label: 'Дольше 30 дней' }, { value: '60', label: 'Дольше 60 дней' },
  { value: '90', label: 'Дольше 90 дней' }, { value: '180', label: 'Дольше полугода' }];
const REACH_OPTS = [{ value: '', label: 'Все' }, { value: 'yes', label: 'Доходят' }, { value: 'no', label: 'Не доходят' }];
const SEGMENT_OPTS = [{ value: '', label: 'Все' }, { value: 'not_bought', label: 'Ещё не покупали' }, { value: 'new', label: 'Новые покупатели' },
  { value: 'loyal', label: 'Активные' }, { value: 'leaving', label: 'Пауза 2–6 мес.' }, { value: 'lost', label: 'Пауза больше 6 мес.' },
  { value: 'one_time', label: 'Купили один раз' }];
const FIELD_LABEL = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#333' };

function readPresetFilter() {
  try {
    const raw = sessionStorage.getItem('nf_clients_filter');
    if (raw) {
      sessionStorage.removeItem('nf_clients_filter');
      return JSON.parse(raw) || {};
    }
  } catch (e) { /* no-op */ }
  return {};
}

function ClientsPage({ showToast }) {
  // Фильтры и покупки клиентов — только владельцу (владелец 28.10); администратору — список и поиск
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [stats, setStats] = useState({ total_users: 0, total_bonus_balance: 0, tg_users: 0, max_users: 0, web_users: 0 });
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Поиск и сортировка
  const [searchUsernameInput, setSearchUsernameInput] = useState('');
  const [searchTelegramIdInput, setSearchTelegramIdInput] = useState('');
  const [searchPhoneInput, setSearchPhoneInput] = useState('');
  const [searchUsername, setSearchUsername] = useState('');
  const [searchTelegramId, setSearchTelegramId] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [flt, setFlt] = useState(() => (isOwner ? readPresetFilter() : {}));
  const [sources, setSources] = useState([]);
  const [total, setTotal] = useState(null);
  const fltKey = JSON.stringify(flt);
  const setF = (k, v) => setFlt(f => ({ ...f, [k]: v }));
  // Дата: пока год набирается по цифре (0002 → 2026), в фильтр не берём — иначе запрос на каждую цифру
  const setDate = (k, v) => { if (!v || v.slice(0, 4) >= '2000') setF(k, v); };
  // Ответ на старый фильтр, пришедший позже нового, не показываем (ревью 27.10)
  const reqId = useRef(0);

  // Просмотр заказа из истории
  const [selectedOrderView, setSelectedOrderView] = useState(null);

  // Модальное окно клиента
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [clientOrders, setClientOrders] = useState([]);
  const [ordersOffset, setOrdersOffset] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Форма начисления/списания
  const [bonusForm, setBonusForm] = useState({ amount: '', comment: '' });
  const [bonusAction, setBonusAction] = useState(null); // 'add' or 'subtract'

  // Объединение/разъединение аккаунтов
  const [mergeMode, setMergeMode] = useState(false); // показывать форму поиска
  const [mergeSearchInput, setMergeSearchInput] = useState('');
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null); // выбранный для merge
  const [merging, setMerging] = useState(false);
  const [unmergeConfirm, setUnmergeConfirm] = useState(null); // 'telegram' | 'max' | 'web' | 'choose' | null
  const [unmergePreview, setUnmergePreview] = useState(null); // { orders_count, orders_total_sum, bonus_balance }
  const [unmergeBonusRecipient, setUnmergeBonusRecipient] = useState('main'); // 'main' | 'split'

  useEffect(() => {
    loadStats();
    if (isOwner) api.getUserSources().then(setSources).catch(() => {});
  }, []);

  // Загрузка и перезагрузка при изменении поиска/сортировки/фильтров
  useEffect(() => {
    loadClients();
  }, [searchUsername, searchTelegramId, searchPhone, sortBy, sortOrder, fltKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (field) => {
    if (field === 'username') {
      setSearchUsername(searchUsernameInput.trim());
      setSearchTelegramIdInput(''); setSearchTelegramId('');
      setSearchPhoneInput(''); setSearchPhone('');
    } else if (field === 'platformId') {
      setSearchTelegramId(searchTelegramIdInput.trim());
      setSearchUsernameInput(''); setSearchUsername('');
      setSearchPhoneInput(''); setSearchPhone('');
    } else if (field === 'phone') {
      setSearchPhone(searchPhoneInput.trim());
      setSearchUsernameInput(''); setSearchUsername('');
      setSearchTelegramIdInput(''); setSearchTelegramId('');
    }
  };

  const openOrderView = async (orderId) => {
    try {
      const order = await api.getOrderById(orderId);
      setSelectedOrderView(order);
    } catch (error) {
      showToast('Ошибка загрузки заказа', 'error');
    }
  };

  const loadStats = async () => {
    try {
      const data = await api.getUsersStats();
      setStats(data);
    } catch (error) {
      showToast('Ошибка загрузки статистики', 'error');
    }
  };

  const loadClients = async () => {
    const my = ++reqId.current;
    setIsLoading(true);
    try {
      const data = await api.getUsers({
        searchUsername,
        searchPlatformId: searchTelegramId,
        searchPhone,
        sortBy,
        sortOrder,
        limit: 100,
        offset: 0,
        filters: flt,
      });
      if (my !== reqId.current) return;
      setClients(data.users || []);
      setHasMore(data.has_more || false);
      setTotal(data.total ?? null);
    } catch (error) {
      if (my === reqId.current) showToast('Ошибка загрузки клиентов', 'error');
    } finally {
      if (my === reqId.current) setIsLoading(false);
    }
  };

  const loadMoreClients = async () => {
    const my = reqId.current;
    setLoadingMore(true);
    try {
      const data = await api.getUsers({
        searchUsername,
        searchPlatformId: searchTelegramId,
        searchPhone,
        sortBy,
        sortOrder,
        limit: 100,
        offset: clients.length,
        filters: flt,
      });
      if (my !== reqId.current) return;   // фильтр сменился, пока грузили
      setClients(prev => [...prev, ...(data.users || [])]);
      setHasMore(data.has_more || false);
    } catch (error) {
      showToast('Ошибка загрузки', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSortClick = (column) => {
    if (sortBy === column) {
      // Циклически переключаем: desc → asc → none
      if (sortOrder === 'desc') {
        setSortOrder('asc');
      } else if (sortOrder === 'asc') {
        setSortBy('');
        setSortOrder('desc');
      }
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕';
    return sortOrder === 'desc' ? '↓' : '↑';
  };

  const openClientModal = async (client) => {
    setSelectedClient(client);
    setShowModal(true);
    setBonusAction(null);
    setBonusForm({ amount: '', comment: '' });
    setOrdersOffset(0);
    setClientOrders([]);

    // Загружаем полную историю бонусов (заказы + корректировки)
    loadClientOrders(client.id, 0);
  };

  const loadClientOrders = async (userId, offset) => {
    setLoadingOrders(true);
    try {
      const data = await api.getUserOrders(userId, 10, offset);
      if (offset === 0) {
        setClientOrders(data.orders || []);
      } else {
        setClientOrders(prev => [...prev, ...(data.orders || [])]);
      }
      setOrdersOffset(offset + 10);
      setHasMoreOrders(data.has_more || false);
    } catch (error) {
      showToast('Ошибка загрузки истории', 'error');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleLoadMoreOrders = () => {
    if (selectedClient) {
      loadClientOrders(selectedClient.id, ordersOffset);
    }
  };

  const handleBonusAction = async (action) => {
    if (!bonusForm.amount || parseFloat(bonusForm.amount) <= 0) {
      showToast('Введите корректную сумму', 'error');
      return;
    }

    if (!bonusForm.comment.trim()) {
      showToast('Укажите комментарий', 'error');
      return;
    }

    try {
      const amount = parseFloat(bonusForm.amount);
      const comment = bonusForm.comment.trim();

      const clientId = selectedClient.id;
      if (action === 'add') {
        const updatedUser = await api.addUserBonus(clientId, amount, comment);
        showToast(`Счёт ${BRAND_NAME} пополнен`);

        setSelectedClient(prev => ({ ...prev, bonus_balance: updatedUser.bonus_balance }));
        setClients(prev => prev.map(c =>
          c.id === clientId
            ? { ...c, bonus_balance: updatedUser.bonus_balance }
            : c
        ));
      } else if (action === 'subtract') {
        const updatedUser = await api.subtractUserBonus(clientId, amount, comment);
        showToast(`Списано со счёта ${BRAND_NAME}`);

        setSelectedClient(prev => ({ ...prev, bonus_balance: updatedUser.bonus_balance }));
        setClients(prev => prev.map(c =>
          c.id === clientId
            ? { ...c, bonus_balance: updatedUser.bonus_balance }
            : c
        ));
      }

      // Перезагружаем историю бонусов
      loadClientOrders(clientId, 0);

      // Обновляем статистику
      loadStats();

      // Сбрасываем форму
      setBonusForm({ amount: '', comment: '' });
      setBonusAction(null);
    } catch (error) {
      showToast(error.message || 'Ошибка операции', 'error');
    }
  };

  // Поиск юзера для merge
  const handleMergeSearch = async () => {
    const q = mergeSearchInput.trim();
    if (!q) return;
    setMergeSearching(true);
    try {
      const data = await api.getUsers({ searchTelegramId: q, limit: 10, offset: 0 });
      // Исключаем текущего клиента
      const filtered = (data.users || []).filter(u => u.id !== selectedClient.id);
      setMergeSearchResults(filtered);
    } catch (e) {
      showToast('Ошибка поиска', 'error');
    } finally {
      setMergeSearching(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeTarget) return;
    setMerging(true);
    try {
      const result = await api.mergeUsers(mergeTarget.id, selectedClient.id);
      showToast(result.message || 'Аккаунты объединены');
      // Обновляем selectedClient
      if (result.user) setSelectedClient(result.user);
      // Сбрасываем
      setMergeMode(false);
      setMergeTarget(null);
      setMergeSearchResults([]);
      setMergeSearchInput('');
      loadClients();
      loadStats();
      if (result.user?.id) loadClientOrders(result.user.id, 0);
    } catch (e) {
      showToast(e.message || 'Ошибка объединения', 'error');
    } finally {
      setMerging(false);
    }
  };

  const handlePickUnmergePlatform = async (platform) => {
    setUnmergeConfirm(platform);
    setUnmergeBonusRecipient('main');
    setUnmergePreview(null);
    try {
      const data = await api.getUnmergePreview(selectedClient.id, platform);
      setUnmergePreview(data);
    } catch (e) {
      showToast('Не удалось загрузить превью', 'error');
    }
  };

  const handleUnmerge = async (platform) => {
    try {
      const result = await api.unmergeUser(selectedClient.id, platform, unmergeBonusRecipient);
      showToast(result.message || 'Аккаунт разъединён');
      if (result.user) setSelectedClient(result.user);
      setUnmergeConfirm(null);
      setUnmergePreview(null);
      setUnmergeBonusRecipient('main');
      loadClients();
      loadStats();
    } catch (e) {
      showToast(e.message || 'Ошибка разъединения', 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    const d = String(phone).replace(/\D/g, '').slice(-10);
    if (d.length !== 10) return phone;
    return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
  };

  const getClientDisplayName = (c) => {
    const tgName = [c.tg_first_name, c.tg_last_name].filter(Boolean).join(' ');
    const maxName = c.max_first_name || '';
    const webName = c.web_first_name || '';
    const parts = [];
    if (c.telegram_id && tgName) parts.push(tgName);
    if (c.max_id && maxName) parts.push(maxName);
    if (c.phone && webName) parts.push(webName);
    if (parts.length) return parts.join(' / ');
    return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || '';
  };


  const statusLabels = {
    ...ORDER_STATUS, // единые подписи заказов (src/orderStatus.js)
    // Сертификатные статусы
    pending_payment: { label: 'Ожидает оплаты', class: 'new' },
    active: { label: 'Активен', class: 'ready' },
    activated: { label: 'Активирован', class: 'completed' },
    activation: { label: 'Активация', class: 'completed' },
    expired: { label: 'Истёк', class: 'cancelled' },
  };

  // Полный экран загрузки — только в первый раз; смена фильтра — список приглушается, фильтры на месте
  if (isLoading && total === null && !clients.length) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Клиенты</h1>
      </div>

      {/* Статистика */}
      <div className="stats-grid clients-stats">
        {[
          { label: 'Всего клиентов', value: stats.total_users, tone: 'dark', Icon: IconUsers },
          { label: 'Telegram', value: stats.tg_users, tone: 'primary', Icon: IconTelegram },
          { label: 'Max', value: stats.max_users, tone: 'sand', Icon: IconMax },
          { label: 'Web', value: stats.web_users, tone: 'ochre', Icon: IconWeb },
          { label: `Сумма на счетах ${BRAND_NAME}`, value: `${Number(stats.total_bonus_balance || 0).toLocaleString('ru-RU')} ₽`, tone: 'accent', Icon: IconWallet },
        ].map(({ label, value, tone, Icon }) => (
          <div key={label} className={`stat-card stat-card-${tone}`}>
            <div className="stat-icon"><Icon /></div>
            <div style={{ minWidth: 0 }}>
              <div className="stat-label">{label}</div>
              <div className="stat-value">{typeof value === 'number' ? value.toLocaleString('ru-RU') : value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Поиск. «Найти» — второстепенные: трёх главных на экране быть не должно (UX-аудит 25.10) */}
      <div style={{
        background: 'white',
        '--ch': '12px', border: '1px solid #D8DDDA', '--stroke': '#D8DDDA',
        padding: '16px',
        marginBottom: '20px',
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '6px',
              color: '#333'
            }}>
              Поиск по имени и username TG
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Введите имя..."
                  value={searchUsernameInput}
                  onChange={(e) => setSearchUsernameInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch('username')}
                  style={{ width: '100%', paddingRight: searchUsernameInput ? '32px' : undefined }}
                />
                {searchUsernameInput && (
                  <button
                    onClick={() => { setSearchUsernameInput(''); setSearchUsername(''); }}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#999', fontSize: '16px', lineHeight: 1, padding: '0',
                    }}
                  >×</button>
                )}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => handleSearch('username')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Найти
              </button>
            </div>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '6px',
              color: '#333'
            }}>
              Поиск по Telegram ID / Max ID
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Введите ID..."
                  value={searchTelegramIdInput}
                  onChange={(e) => setSearchTelegramIdInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch('platformId')}
                  style={{ width: '100%', paddingRight: searchTelegramIdInput ? '32px' : undefined }}
                />
                {searchTelegramIdInput && (
                  <button
                    onClick={() => { setSearchTelegramIdInput(''); setSearchTelegramId(''); }}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#999', fontSize: '16px', lineHeight: 1, padding: '0',
                    }}
                  >×</button>
                )}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => handleSearch('platformId')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Найти
              </button>
            </div>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '6px',
              color: '#333'
            }}>
              Поиск по телефону
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="+7 (926) 123-45-67"
                  value={searchPhoneInput}
                  onChange={(e) => setSearchPhoneInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch('phone')}
                  style={{ width: '100%', paddingRight: searchPhoneInput ? '32px' : undefined }}
                />
                {searchPhoneInput && (
                  <button
                    onClick={() => { setSearchPhoneInput(''); setSearchPhone(''); }}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#999', fontSize: '16px', lineHeight: 1, padding: '0',
                    }}
                  >×</button>
                )}
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={() => handleSearch('phone')}
                style={{ whiteSpace: 'nowrap' }}
              >
                Найти
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Фильтры (27.10) — только владельцу */}
      {isOwner && <div style={{ background: 'white', '--ch': '12px', border: '1px solid #D8DDDA', '--stroke': '#D8DDDA', padding: '16px', marginBottom: '20px' }}>
        <div className="clients-filters">
          <div>
            <label style={FIELD_LABEL}>Пришли</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" className="form-input" value={flt.joined_from || ''} onChange={(e) => setDate('joined_from', e.target.value)} aria-label="Пришли с" />
              <span style={{ color: '#999' }}>—</span>
              <input type="date" className="form-input" value={flt.joined_to || ''} onChange={(e) => setDate('joined_to', e.target.value)} aria-label="Пришли по" />
            </div>
          </div>
          <div>
            <label style={FIELD_LABEL}>Где</label>
            <Select value={flt.platform || ''} onChange={(v) => setF('platform', v)} options={PLATFORM_OPTS} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Откуда пришли</label>
            <Select value={flt.source || ''} onChange={(v) => setF('source', v)}
              options={[{ value: '', label: 'Все' }, ...sources.map(x => ({ value: x.key, label: `${x.title} · ${x.count}` }))]} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Покупки</label>
            <Select value={flt.bought || ''} onChange={(v) => setF('bought', v)} options={BOUGHT_OPTS} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Не заказывали</label>
            <Select value={flt.idle_days ? String(flt.idle_days) : ''} onChange={(v) => setF('idle_days', v)} options={IDLE_OPTS} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Сообщения бота</label>
            <Select value={flt.reach || ''} onChange={(v) => setF('reach', v)} options={REACH_OPTS} />
          </div>
          <div>
            <label style={FIELD_LABEL}>Группа</label>
            <Select value={flt.segment || ''} onChange={(v) => setF('segment', v)} options={SEGMENT_OPTS} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 12, fontSize: 14 }}>
          <span>Найдено: <b>{total == null ? '…' : total.toLocaleString('ru-RU')}</b></span>
          {Object.values(flt).some(v => v !== '' && v != null) && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFlt({})}>Сбросить фильтры</button>
          )}
          {flt.reach && <span style={{ color: '#666', fontSize: 13 }}>Доходят ли сообщения — по последним рассылкам</span>}
        </div>
      </div>}

      {/* Таблица клиентов */}
      <div className="table-container" style={{ opacity: isLoading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Имя (Telegram/Max/Web)</th>
              <th>Username TG</th>
              <th>Telegram ID</th>
              <th>Max ID</th>
              <th>Телефон</th>
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSortClick('bonus_balance')}
              >
                Счёт {BRAND_NAME} {getSortIcon('bonus_balance')}
              </th>
              {isOwner && (<>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSortClick('purchases')}
                  title="Дни с оплаченным заказом и сумма">
                  Покупки {getSortIcon('purchases')}
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSortClick('last_order')}>
                  Последний заказ {getSortIcon('last_order')}
                </th>
              </>)}
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSortClick('created_at')}
              >
                Дата регистрации {getSortIcon('created_at')}
              </th>
              <th>Откуда пришёл</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan={isOwner ? 10 : 8} style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                  Клиентов не найдено
                </td>
              </tr>
            ) : (
              clients.map(client => {
                const displayName = getClientDisplayName(client);
                const tgUsername = client.tg_username || client.username;
                const formattedPhone = formatPhone(client.phone);
                return (
                  <tr key={client.id} style={{ cursor: 'pointer' }} onClick={() => openClientModal(client)}>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1C3D2E' }}>
                      {displayName || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', color: tgUsername ? '#357156' : '#999' }}>
                      {tgUsername ? `@${tgUsername}` : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '13px' }}>
                      {client.telegram_id ? (
                        <>
                          {client.telegram_id}
                          <span style={{ marginLeft: 3, fontSize: 10, color: '#245640', background: '#DFF2E8', padding: '1px 4px', borderRadius: 3 }}>TG</span>
                        </>
                      ) : <span style={{ color: '#999' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '13px' }}>
                      {client.max_id ? (
                        <>
                          {client.max_id}
                          <span style={{ marginLeft: 3, fontSize: 10, color: '#474D49', background: '#EBEDEC', padding: '1px 4px', borderRadius: 3 }}>Max</span>
                        </>
                      ) : <span style={{ color: '#999' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '13px', color: formattedPhone ? '#333' : '#999' }}>
                      {formattedPhone || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#B0603C' }}>
                      {client.bonus_balance} ₽
                    </td>
                    {isOwner && (<>
                      <td style={{ padding: '8px 12px', fontSize: '13px', color: client.purchases ? '#1C3D2E' : '#999', whiteSpace: 'nowrap' }}>
                        {client.purchases ? `${client.purchases} · ${(client.spent || 0).toLocaleString('ru-RU')} ₽` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#666', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        {client.last_order_at ? new Date(client.last_order_at).toLocaleDateString('ru-RU') : '—'}
                      </td>
                    </>)}
                    <td style={{ padding: '8px 12px', color: '#666', fontSize: '13px' }}>
                      {formatDate(client.created_at)}
                    </td>
                    {/* Первый переход клиента — справочно (15.10); до 13.10 источники не записывались */}
                    <td style={{ padding: '8px 12px', fontSize: '13px', color: client.first_source ? '#1C3D2E' : '#999' }}>
                      {client.first_source || '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Кнопка "Загрузить еще" */}
      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            className="btn btn-secondary"
            onClick={loadMoreClients}
            disabled={loadingMore}
          >
            {loadingMore ? 'Загрузка...' : 'Загрузить еще'}
          </button>
        </div>
      )}

      {/* Модальное окно клиента */}
      {showModal && selectedClient && (
        <ClientProfileModal
          clientId={selectedClient.id}
          onClose={() => setShowModal(false)}
          showToast={showToast}
          onClientChange={(u) => {
            setSelectedClient(prev => prev ? { ...prev, ...u } : prev);
            setClients(prev => prev.map(c => c.id === u.id ? { ...c, ...u } : c));
            loadStats();
          }}
        />
      )}

    </div>
  );
}

export default ClientsPage;
